import { cn } from 'utils/cn';
import { type CategoryTreeCategory } from './signals';
import { useCallback } from 'preact/hooks';
import { SpinnerDots } from 'components/atoms/spinner/SpinnerDots';
import { number } from 'utils/format';
import { activeCategory } from 'signals/category';
import { Icon } from 'components/atoms/Icon';
import { CategoryItemImage } from 'components/modal/categories/CategoryItemImage';
import { getCategoryImage } from 'utils/categoryIdToImagePath';
import type { CategoryData } from 'service/category/model.ts';

export type Props = {
	category: CategoryTreeCategory | CategoryData;
	className?: string;
	showDropdownIcon?: boolean;
	showCount?: boolean;
	isActive?: boolean;
	onClick?: (category: CategoryTreeCategory | CategoryData, event?: MouseEvent) => void;
};

export function CategoryItem({
	category,
	className,
	onClick,
	showDropdownIcon = true,
	showCount = true,
	isActive,
}: Props) {
	const handleClick = useCallback(
		(event: MouseEvent) => {
			onClick?.(category, event);
		},
		[category],
	);

	const offersCount =
		category?.id === 0 ? '' : category?.offersCount !== undefined ? number(category.offersCount) : <SpinnerDots />;
	const categoryImage = category && getCategoryImage(category.id);
	const _isActive =
		isActive !== undefined
			? isActive
			: activeCategory.value?.id === category?.id || (!activeCategory.value && category?.id === 0);

	return (
		<button
			data-test="category-item"
			class={cn(
				'flex gap-3 justify-between items-center bg-gray-category rounded-2xl px-3 py-2 md:py-3 text-sm font-medium hover:bg-gray-categoryActive h-full cursor-pointer w-full',
				_isActive && 'bg-gray-categoryActive',
				category.id === 0 && 'pl-5 leading-9',
				className,
			)}
			onClick={handleClick}
		>
			<span class="inline-flex justify-start items-center">
				{category.id !== 0 && <CategoryItemImage categoryImage={categoryImage} categoryName={category.name} />}{' '}
				<span class={cn('flex-shrink gap-1 text-left line-clamp-2 leading-5', _isActive && 'font-bold')}>
					{category.name}
					{showDropdownIcon && 'children' in category && !!category.children.length && (
						<span class="whitespace-nowrap">
							{' '}
							<Icon type="keyboard_arrow_down" className="relative top-1" size={16} />
						</span>
					)}
				</span>
			</span>{' '}
			{showCount && <span class="text-xs leading-5 text-dark-blue-50 shrink-0">{offersCount}</span>}
		</button>
	);
}
